// Package imports:
import React, { useState } from "react";
// Component imports:
import Modal from "../../ui-elements/Modal/Modal";
import Link from "../../ui-elements/Link/Link";
import Alert from "../../ui-elements/Alert/Alert";
import { LinkedIndividual, PepCompanyResult } from "../../types/ForeignCompanyPepTypes";
import { formatCategories } from "./PepPage";
import { CountryList } from "../../assets/staticData/CountryList";
import PepReviewControls, { PepReviewUpdate } from "./PepReviewControls";

interface IProps {
    show: boolean;
    close(): void;
    data: PepCompanyResult | undefined;
    /** The search parameters this report was created from, used to title the modal. */
    request?: { companyName?: string; country?: string };
    /** The report row's id (pepReportId). When provided, the false-positive / comment controls are shown. */
    pepReportId?: number;
    /** Current whole-report false-positive verdict. */
    falsePositive?: boolean;
    /** Current reviewer comment. */
    comment?: string | null;
    /** Called after a successful flag/comment write so the parent can stay in sync. */
    onReviewChange?: (reportId: number, update: PepReviewUpdate) => void;
}


const ForeignCompanyPepModal: React.FC<IProps> = ({ show, close, data, request, pepReportId, falsePositive, comment, onReviewChange }) => {
    // Build the modal title from the search parameters (company name, and country when present).
    const buildSearchTitle = (): string | undefined => {
        if (!request?.companyName) return undefined;
        const country = request.country
            ? CountryList.find(c => c.value === request.country)?.label ?? request.country
            : undefined;
        return country ? `${request.companyName} (${country})` : request.companyName;
    };
    const searchTitle = buildSearchTitle();
    const acceptedKeys: (keyof PepCompanyResult)[] = [
        "category",
        "categories",
        "matchRate",
        "match_rate",
        "matchedFields",
        "companyName",
        "locations",
        "linkedIndividuals",
        "generalInfo"
    ];

    const renderValue = (value: any, key?: keyof PepCompanyResult) => {
        //special formatin forr text
        if (key === "categories") return formatCategories(value);
        if (key === 'matchedFields') return Array.isArray(value) ? value.join(', ') : value;
        if (key === "generalInfo") return value.businessDescription ?? "-";
        if (key === 'locations') {
            if (Array.isArray(value) && value.length > 0) {
                return value.map((loc: any, i: number) => (
                    <>
                        <div style={{ marginTop: '10px', marginBottom: '10px' }} key={`${loc}-${i}`}>
                            {loc.address.length > 0 ? loc.address : CountryList.find(c => c.value === loc.countryCode)?.label ?? '-'}
                        </div>
                    </>
                ));
            }
            return '-';
        }
        return value === "" || value === null ? "-" : value;
    };
    const renderTitle = (key?: keyof PepCompanyResult) => {
        if (key === 'matchRate' || key === 'match_rate') return 'Match rate';
        if (key === 'matchedFields') return 'Matched Fields';
        if (key === 'companyName') return 'Company Name';
        if (key === 'generalInfo') return 'Description';
        return key;
    };

    const modalBody = () => {
        if (!data) return <Alert type={"alert"} headText={"Engin gögn fundust"} />;
        return (
            <div>
                {data.resourceId &&
                    <Link targetBlank linkSize="15" url={`/Pdf/Althjodlegir-valistar-logadilar-profile/${data.resourceId}`} icon="pdf">
                        Sækja skýrslu
                    </Link>
                }
                <div className="KCL_foreignPepModal">
                    {Object.keys(data).map((key, index) => {
                        if (acceptedKeys.includes(key as keyof PepCompanyResult)) {
                            if (key === "linkedIndividuals") {
                                return <ExpandableRow
                                    key={key}
                                    title={'Linked Individuals'}
                                    items={data[key]}
                                />
                            }
                            const longValue = key === 'locations' || key === 'categories' || key === 'generalInfo';
                            return (
                                <div className="item_container" key={index}>
                                    <span className={['item_key', longValue && 'long_value'].filter(Boolean).join(' ')}>
                                        {renderTitle(key as keyof PepCompanyResult)}
                                    </span>
                                    <span className="item_value">
                                        {renderValue(
                                            data[key as keyof PepCompanyResult],
                                            key as keyof PepCompanyResult
                                        )}
                                    </span>
                                </div>
                            );
                        } else {
                            return null;
                        }
                    })}
                </div>
            </div>
        );
    };
    return <Modal
        header={searchTitle ?? data?.companyName}
        show={show}
        onHide={close}
        hasCloseButton
        body={modalBody()}
        footer={pepReportId !== undefined && pepReportId !== null
            ? <PepReviewControls
                key={pepReportId}
                pepReportId={pepReportId}
                initialFalsePositive={falsePositive ?? false}
                initialComment={comment ?? null}
                onReviewChange={onReviewChange}
            />
            : undefined
        }
    />;
};

export default ForeignCompanyPepModal;


interface ExpandableRowProps {
    items: LinkedIndividual[] | undefined;
    title: string;
}

const ExpandableRow: React.FC<ExpandableRowProps> = ({ items, title }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isExpandable = items && items.length > 2;

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={` item_container individualRow ${isExpanded ? 'expanded' : 'collapsed'} ${!isExpandable && 'notExpandable'}`} onClick={toggleExpand}>
            <span className="item_key">
                {title}
            </span>
            <div className="item_value">
                {items?.slice(0, !isExpanded ? 2 : undefined).map((individual, index) => (
                    <div key={index}>
                        <div style={{ fontWeight: 500 }}>
                            {individual.firstName && <span>{individual.firstName}&nbsp;</span>}
                            {individual.middleName && <span>{individual.middleName}&nbsp;</span>}
                            {individual.lastName && <span>{individual.lastName}</span>}
                        </div>
                        <div>
                            {individual.description && <span>{individual.description}</span>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
